import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, IconButton, Drawer, Popover, useTheme, useMediaQuery } from '@mui/material';
import { colors, spacing, radius } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import { IconHot } from 'components/common/IconHot';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import CalendarIconSvg from 'assets/personal_zone/Calendar.svg';
import { SmallCountBadge } from './CountBadge';
import { CouponItem } from './CouponItem';
import { getMediaPath, generateTestId, preventBodyScroll } from 'app/functions';
import { useGetUserCouponsForBenefitMutation } from 'redux/api/commerce.api';
import { transformPaidCoupons, transformFreeCoupon, getCouponsFromBenefitResponse } from '../../utils/functions';
import { COUPON_STATUS, FILTER_BENEFITS_TYPES } from '../../utils/enums';
import Loader from 'components/common/loader';
import { handleBenefitNameClick } from '../../utils/functions';
import { useTranslations } from 'common/hooks/useTranslations';



export const BenefitCard = ({
  benefit,
  isExpanded,
  onToggle,
  testID,
  actionsBaseTestID: externalActionsBaseTestID,
  onArchive,
  onCancel,
  activeCancelCouponId,
  isPaidCoupons = true,
  activeFilters,
  onCouponStatusUpdate,
  onCouponArchiveUpdate
}) => {
  const [coupons, setCoupons] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef(null);
  const justRemovedCouponRef = useRef(false);
  const cardRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastFetchedPageRef = useRef(-1);
  const savedScrollPositionRef = useRef(0);

  const [getCoupons, { isLoading }] = useGetUserCouponsForBenefitMutation();
  const { translate } = useTranslations();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const baseTestID = testID || 'benefit-card';
  const actionsBaseTestID = externalActionsBaseTestID || generateTestId(baseTestID, 'actions');
  useEffect(() => {
    if (onCouponStatusUpdate && benefit.id) {
      const updateStatus = (benefitId, couponCode, newStatus, filters) => {
        if (benefitId === benefit.id) {
          setCoupons(prev => {
            const selectedFilter = filters?.selectedFilter || FILTER_BENEFITS_TYPES.ACTIVE;
            const shouldFilterOut = newStatus === COUPON_STATUS.REFUND && 
              selectedFilter === FILTER_BENEFITS_TYPES.ACTIVE;
            
            if (shouldFilterOut) {
              justRemovedCouponRef.current = true;
              setTimeout(() => {
                justRemovedCouponRef.current = false;
              }, 500);
              return prev.filter(coupon => {
                const codeMatch = coupon.code === couponCode || coupon.id === couponCode || String(coupon.id) === String(couponCode);
                return !codeMatch;
              });
            }
            
            return prev.map(coupon => {
              const codeMatch = coupon.code === couponCode || coupon.id === couponCode || String(coupon.id) === String(couponCode);
              if (codeMatch) {
                return { ...coupon, status: newStatus };
              }
              return coupon;
            });
          });
        }
      };
      onCouponStatusUpdate(benefit.id, updateStatus);
      return () => {
        onCouponStatusUpdate(benefit.id, null);
      };
    }
  }, [onCouponStatusUpdate, benefit.id]);

  useEffect(() => {
    if (onCouponArchiveUpdate && benefit.id && isPaidCoupons) {
      const updateArchive = (benefitId, couponCode, isArchived) => {
        if (benefitId !== benefit.id) return;
        const selectedFilter = activeFilters?.selectedFilter || FILTER_BENEFITS_TYPES.ACTIVE;
        const shouldRemove = (selectedFilter === FILTER_BENEFITS_TYPES.ACTIVE && isArchived) ||
          (selectedFilter === FILTER_BENEFITS_TYPES.ARCHIVED && !isArchived);
        if (shouldRemove) {
          justRemovedCouponRef.current = true;
          setTimeout(() => { justRemovedCouponRef.current = false; }, 500);
          setCoupons(prev => prev.filter(coupon => {
            const codeMatch = coupon.code === couponCode || coupon.id === couponCode || String(coupon.id) === String(couponCode);
            return !codeMatch;
          }));
        } else {
          setCoupons(prev => prev.map(coupon => {
            const codeMatch = coupon.code === couponCode || coupon.id === couponCode || String(coupon.id) === String(couponCode);
            return codeMatch ? { ...coupon, is_archived: isArchived } : coupon;
          }));
        }
      };
      onCouponArchiveUpdate(benefit.id, updateArchive);
      return () => onCouponArchiveUpdate(benefit.id, null);
    }
  }, [onCouponArchiveUpdate, benefit.id, isPaidCoupons, activeFilters?.selectedFilter]);

  useEffect(() => {
    if (!isPaidCoupons && isExpanded) {
      let allItems = [];
      benefit?.orders?.forEach(order => {
        order.order_items?.forEach(item => {
          allItems.push(transformFreeCoupon(item, order.order_id, order.order_date));
        });
      });

      setCoupons(allItems);
      setHasMore(false);
    }
  }, [isExpanded, isPaidCoupons, benefit]);

  useEffect(() => {
    if (justRemovedCouponRef.current) {
      return;
    }
    if (isPaidCoupons) {
      setCoupons([]);
      setPage(1);
      setHasMore(true);
      isFetchingRef.current = false;
      lastFetchedPageRef.current = -1;
    }
  }, [isExpanded, benefit.id, benefit.count, activeFilters, isPaidCoupons]);

  const fetchCoupons = useCallback(async (pageNum) => {
    if (!isPaidCoupons || !hasMore || isLoading || isFetchingRef.current || lastFetchedPageRef.current === pageNum) return;

    isFetchingRef.current = true;
    lastFetchedPageRef.current = pageNum;

    const selectedFilter = activeFilters?.selectedFilter || FILTER_BENEFITS_TYPES.ACTIVE;

    try {
      const response = await getCoupons({
        benefitId: benefit.id,
        page: pageNum,
        pageNum: pageNum,
        filterBy: selectedFilter
      }).unwrap();

      const rawCoupons = getCouponsFromBenefitResponse(response);
      const newCoupons = transformPaidCoupons(benefit, rawCoupons);
      setCoupons(prev => [...prev, ...newCoupons]);

      if (newCoupons.length === 0 || rawCoupons.length < 5) {
        setHasMore(false);
      } else {
        setPage(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to fetch coupons", error);
      setHasMore(false);
    } finally {
      isFetchingRef.current = false;
    }
  }, [benefit.id, benefit.count, hasMore, isLoading, activeFilters, getCoupons, isPaidCoupons]);

  useEffect(() => {
    if (isPaidCoupons && isExpanded && coupons.length === 0 && hasMore && !isLoading && page === 1 && lastFetchedPageRef.current !== 1) {
      fetchCoupons(1);
    }
  }, [isExpanded, coupons.length, hasMore, isLoading, fetchCoupons, isPaidCoupons, page]);

  useEffect(() => {
    if (isExpanded && isMobile) {
      savedScrollPositionRef.current = window.scrollY || window.pageYOffset;
      preventBodyScroll(true);
      return () => {
        preventBodyScroll(false);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({
              top: savedScrollPositionRef.current,
              behavior: 'instant'
            });
          });
        });
      };
    }
  }, [isExpanded, isMobile]);

  const handleScroll = (e) => {
    e.stopPropagation();
    const container = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    if (scrollTop + clientHeight >= scrollHeight - 1) {
      container.scrollTop = scrollHeight - clientHeight;
    }
    
    if (isPaidCoupons && scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isLoading && !isFetchingRef.current) {
      if (page > 1 && coupons.length > 0) {
        fetchCoupons(page);
      }
    }
  };

  const handleWheel = (e) => {
    e.stopPropagation();
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const deltaY = e.deltaY;
    
    const isAtTop = scrollTop === 0 && deltaY < 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1 && deltaY > 0;
    
    if (isAtTop || isAtBottom) {
      e.preventDefault();
    }
  };

  const handleTouchMove = (e) => {
    e.stopPropagation();
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    if (scrollTop <= 0) {
      container.scrollTop = 0;
    } else if (scrollTop + clientHeight >= scrollHeight) {
      container.scrollTop = scrollHeight - clientHeight;
    }
  };

  const handleBackdropWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleBackdropTouchMove = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const renderContent = () => (
    <Box
      testId={generateTestId(baseTestID, 'bottom-sheet-content')}
      dir="rtl"
      onWheel={(e) => {
        const container = scrollContainerRef.current;
        if (!container || !container.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onTouchMove={(e) => {
        const container = scrollContainerRef.current;
        if (!container || !container.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? '80vh' : 'auto',
        maxHeight: '80vh',
        backgroundColor: colors.palette.white,
        overscrollBehavior: 'contain',
      }}
    >
      <Box
        testId={generateTestId(baseTestID, 'header')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: `${spacing.xxl}px`,
          paddingBottom: `${spacing.md}px`,
          gap: `${spacing.md}px`,
          backgroundColor: colors.system.surface.subtle,
        }}
      >
        <Box sx={{ width: 50, height: 50, flexShrink: 0, overflow: 'hidden', borderRadius: `${radius.sm}px` }} testId={generateTestId(baseTestID, 'icon')} onClick={() => handleBenefitNameClick(benefit)}>
          <IconHot src={getMediaPath(benefit?.image)} size={50} />
        </Box>

        <Box testId={generateTestId(baseTestID, 'content-container')} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: `${spacing.md}px` }}>
          <Box testId={generateTestId(baseTestID, 'name-container')} sx={{ display: 'flex', alignItems: 'flex-start', gap: `${spacing.sm}px` }}>
            <TextHot testID={generateTestId(baseTestID, 'name')} size="xl" bold="bold" color="primary" sx={{ direction: 'auto', unicodeBidi: 'plaintext' }}>{benefit?.name || ''}</TextHot>
            <SmallCountBadge count={benefit?.count || 0} isExpanded={true} />
          </Box>
        </Box>

        <IconButton
          testId={generateTestId(baseTestID, 'close-btn')}
          onClick={onToggle}
          size="small"
          sx={{ color: colors.system.text.subtle }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Coupons List */}
      <Box
        testId={generateTestId(baseTestID, 'coupons-container')}
        ref={scrollContainerRef}
        onScroll={handleScroll}
        onWheel={(e) => {
          e.stopPropagation();
          handleWheel(e);
        }}
        onTouchMove={(e) => {
          e.stopPropagation();
          handleTouchMove(e);
        }}
        onTouchStart={(e) => e.stopPropagation()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${spacing.md}px`,
          padding: `${spacing.xxl}px`,
          backgroundColor: colors.system.surface.subtle,
          flex: 1,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {coupons.map((coupon, index) => (
          <CouponItem
            key={`${coupon.id}-${index}`}
            testID={generateTestId(baseTestID, `coupon-${index}`)}
            actionsBaseTestID={actionsBaseTestID}
            coupon={coupon}
            onArchive={(c) => onArchive?.(c, benefit?.id)}
            onCancel={(c, e) => onCancel?.(c, benefit?.id, e)}
            isCancelActive={activeCancelCouponId === coupon.id}
            isPaidCoupons={isPaidCoupons}
            isMultipassCoupon={benefit?.isMultipass}
            benefit={benefit}
            multipassType={benefit?.multipassType}
            hasBorder={true}
          />
        ))}
        {isLoading && isPaidCoupons && (
          <Box testId={generateTestId(baseTestID, 'loader-container')} sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <Loader size="small" />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Card Preview */}
      <Box
        ref={cardRef}
        testId={baseTestID}
        dir="rtl"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colors.palette.white,
          borderRadius: `${radius.md}px`,
          overflow: 'hidden',
        }}
      >
        <Box
          testId={generateTestId(baseTestID, 'preview-header')}
          onClick={onToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: `${spacing.xxl}px`,
            cursor: 'pointer',
            gap: `${spacing.lg}px`,
          }}
        >
          <Box sx={{ width: 78, height: 78, flexShrink: 0, overflow: 'hidden', borderRadius: `${radius.sm}px` }} testId={generateTestId(baseTestID, 'preview-icon')} onClick={() => handleBenefitNameClick(benefit)}>
            <IconHot src={getMediaPath(benefit?.image)} size={78} />
          </Box>

          <Box testId={generateTestId(baseTestID, 'preview-content-container')} sx={{ flex: 1, gap: `${spacing.md}px`, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box testId={generateTestId(baseTestID, 'preview-name-container')} sx={{ display: 'flex', alignItems: 'flex-start', gap: `${spacing.xs}px` }}>
              <TextHot testID={generateTestId(baseTestID, 'preview-name')} size="m" bold="bold" color="primary" sx={{ direction: 'auto', unicodeBidi: 'plaintext' }}>{benefit?.name || ''}</TextHot>
              <SmallCountBadge count={benefit?.count || 0} isExpanded={false} />
            </Box>
            {isPaidCoupons && (
              <TextHot 
                testID={generateTestId(baseTestID, 'preview-savings')}
                size="m" 
                isLtr={true} 
              >
                {translate('my_coupons_table_header_total_savings')}: ₪{Math.round(Number(benefit?.totalSavings))}
              </TextHot>
            )}
            <Box testId={generateTestId(baseTestID, 'preview-date-container')} sx={{ display: 'flex', alignItems: 'center', gap: `${spacing.xs}px` }}>
              <IconHot src={CalendarIconSvg} size={14} color="subtle" />
              <TextHot 
                testID={generateTestId(baseTestID, 'preview-date')}
                size="m" 
                color="subtle"
              >
                {benefit?.latest_order_date || ''}
              </TextHot>
            </Box>
          </Box>

          <IconButton 
            testId={generateTestId(baseTestID, 'preview-toggle-btn')}
            size="small" 
            sx={{ color: colors.system.text.subtle }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Bottom Sheet */}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={isExpanded}
          onClose={onToggle}
          testId={generateTestId(baseTestID, 'drawer')}
          PaperProps={{
            sx: {
              borderRadius: `${radius.xl}px ${radius.xl}px 0 0`,
              maxHeight: '80vh',
            },
            onWheel: handleWheel,
            onTouchMove: handleTouchMove,
          }}
          ModalProps={{
            onWheel: handleBackdropWheel,
            onTouchMove: handleBackdropTouchMove,
            disableScrollLock: false,
            sx: {
              overscrollBehavior: 'contain',
            },
          }}
        >
          {renderContent()}
        </Drawer>
      ) : (
        <Popover
          open={isExpanded}
          anchorEl={cardRef.current}
          onClose={onToggle}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{
            sx: {
              borderRadius: `${radius.lg}px`,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              marginTop: `${spacing.sm}px`,
              maxWidth: '600px',
              width: '90vw',
              maxHeight: '80vh',
            },
            onWheel: handleWheel,
            onTouchMove: handleTouchMove,
          }}
          BackdropProps={{
            onWheel: handleBackdropWheel,
            onTouchMove: handleBackdropTouchMove,
            sx: {
              pointerEvents: 'auto',
              overscrollBehavior: 'contain',
            },
          }}
        >
          {renderContent()}
        </Popover>
      )}
    </>
  );
};

export default BenefitCard;